export const URL_ALLOWED_SCHEME_PATTERN = /^(https?:|mailto:|sms:|tel:)/i;
export const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const DECIMAL_PATTERN = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;
const URL_ALLOWED_SCHEMES = ['http:', 'https:', 'mailto:', 'sms:', 'tel:'];
const URL_VALIDATION_ERROR = 'Enter a URL using https, http, mailto, sms, or tel.';

export const validateUrlAllowedSchemes = (value: unknown): string | undefined => {
  const sanitized = typeof value === 'string' ? value.trim() : value;
  if (!sanitized) return;

  const stringValue = String(sanitized);
  if (!URL_ALLOWED_SCHEME_PATTERN.test(stringValue)) return URL_VALIDATION_ERROR;

  let parsed: URL;
  try {
    parsed = new URL(stringValue);
  } catch {
    return URL_VALIDATION_ERROR;
  }

  if (!URL_ALLOWED_SCHEMES.includes(parsed.protocol)) return URL_VALIDATION_ERROR;
  if ((parsed.protocol === 'http:' || parsed.protocol === 'https:') && !parsed.hostname) return URL_VALIDATION_ERROR;

  return stringValue.slice(parsed.protocol.length) ? undefined : URL_VALIDATION_ERROR;
};

export const validateHexColor = (value: unknown): string | undefined => {
  const sanitized = typeof value === 'string' ? value.trim() : value;
  if (!sanitized) return;
  return HEX_COLOR_PATTERN.test(String(sanitized)) ? undefined : 'Enter a hex color like #FF0000.';
};

export const validateInteger = (value: unknown): string | undefined => {
  const sanitized = typeof value === 'string' ? value.trim() : value;
  if (sanitized === '' || sanitized === null || typeof sanitized === 'undefined') return;

  const num = typeof sanitized === 'number' ? sanitized : Number(sanitized);
  if (!Number.isFinite(num)) return 'Enter a whole number without decimals.';

  return Number.isInteger(num) ? undefined : 'Enter a whole number without decimals.';
};

const WHOLE_NUMBER = /^-?\d+$/;
const SAFE_RANGE = `${Number.MIN_SAFE_INTEGER.toLocaleString('en-US')} to ${Number.MAX_SAFE_INTEGER.toLocaleString('en-US')}`;

/**
 * Digits only, which validateInteger doesn't require (it reads `0x1F` as 31 and `1e3` as 1000), and no more than a JSON
 * number carries exactly. Its message matches validateInteger's.
 */
export const validateWholeNumber = (value: unknown): string | undefined => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (text === '') return undefined;
  if (!WHOLE_NUMBER.test(text)) return 'Enter a whole number without decimals.';
  return Number.isSafeInteger(Number(text)) ? undefined : `Enter a whole number from ${SAFE_RANGE}.`;
};

export const validateDecimal = (value: unknown): string | undefined => {
  const sanitized = typeof value === 'string' ? value.trim() : value;
  if (sanitized === '' || sanitized === null || typeof sanitized === 'undefined') return;

  if (typeof sanitized === 'number') {
    return Number.isFinite(sanitized) ? undefined : 'Enter a valid number.';
  }

  const raw = String(sanitized);

  if (!DECIMAL_PATTERN.test(raw)) return 'Enter a valid number.';

  const num = Number(raw);
  if (!Number.isFinite(num)) return 'Enter a valid number.';

  return undefined;
};
